import { isAxiosError } from 'axios'

/**
 * Converts an arbitrary error thrown by a Work-pipeline stage transition into a
 * user-facing message string.
 *
 * Order matters:
 *   1. Axios network errors (no HTTP response) → friendly "Server not reachable"
 *      message. Without this, the toast just reads "AxiosError: Network Error".
 *   2. Axios errors WITH a response → unwrap the backend's `error` field from
 *      `response.data`. handlePrismaError + the global errorHandler both
 *      serialize ZodErrors as `{ success: false, error: '<field>: <reason>' }`,
 *      so this surfaces the actual field problem (e.g. "lastName: Last name is
 *      required") in the toast rather than the opaque
 *      "AxiosError: Request failed with status code 400". The previous fallback
 *      to `String(err)` hid every backend-supplied message and was the reason
 *      Gaige kept seeing "Request failed with status code 400" with no clue
 *      which field was at fault.
 *   3. Everything else falls through to `String(err)` for back-compat.
 */
export function formatTransitionError(err: unknown): string {
  if (isAxiosError(err)) {
    if (!err.response) {
      return 'Server not reachable — check your connection and try again.'
    }
    const data = err.response.data as
      | { error?: unknown; message?: unknown }
      | null
      | undefined
    const backendMsg =
      (data && typeof data.error === 'string' && data.error) ||
      (data && typeof data.message === 'string' && data.message) ||
      null
    if (backendMsg) return backendMsg
  }
  return String(err)
}
